import React, { useState } from 'react';
import axios from 'axios';
import { Send, MessageSquare, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function WorkflowActions({ 
  contentId, 
  contentType, 
  status, 
  token, 
  onStatusChange,
  rejectionRemark = null,
  rejectedBy = null,
  rejectedAt = null
}) {
  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [remarks, setRemarks] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSendForApproval = async () => {
    if (!window.confirm('Send this content for approval?')) return;

    try {
      const response = await axios.post(
        `${API_URL}/api/workflow/submit`,
        {
          content_id: contentId,
          content_type: contentType,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message || 'Sent for approval successfully!');
      if (onStatusChange) onStatusChange('PENDING');
    } catch (error) {
      console.error('Error submitting for approval:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit for approval');
    }
  };

  const fetchRemarks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/workflow/remarks/${contentType}/${contentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRemarks(response.data.remarks || []);
    } catch (error) {
      console.error('Error fetching remarks:', error);
      toast.error('Failed to load remarks');
    } finally {
      setLoading(false);
    }
  };

  const openRemarksModal = () => {
    fetchRemarks();
    setShowRemarksModal(true);
  };

  return (
    <>
      <div className="flex items-center gap-2">
        {/* No Send button needed - creators auto-submit */}
        {/* Only show View Remarks for PENDING/REJECTED/PUBLISHED */}
        {(status === 'PENDING' || status === 'REJECTED' || status === 'PUBLISHED') && (
          <Button
            onClick={openRemarksModal}
            variant="outline"
            size="sm"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            {status === 'REJECTED' ? 'View Rejection' : 'View Remarks'}
          </Button>
        )}
      </div>

      {/* Remarks Modal */}
      <Dialog open={showRemarksModal} onOpenChange={setShowRemarksModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Workflow Remarks</DialogTitle>
            <DialogDescription>
              View all approval/rejection remarks for this content
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Loading remarks...</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {/* Show current rejection remark if status is REJECTED */}
              {status === 'REJECTED' && rejectionRemark && (
                <div className="p-4 rounded-lg border bg-red-50 border-red-200">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-sm font-semibold text-red-700">
                        ✗ Rejected
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        by {rejectedBy || 'Admin'}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {rejectedAt ? new Date(rejectedAt).toLocaleString() : 'Recently'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{rejectionRemark}</p>
                </div>
              )}
              
              {/* Show historical remarks from content_remarks collection */}
              {remarks.length > 0 ? (
                remarks.map((remark) => {
                  const isRejection = remark.action === 'REJECT';
                  return (
                    <div
                      key={remark.id}
                      className={`p-4 rounded-lg border ${
                        isRejection ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className={`text-sm font-semibold ${
                            isRejection ? 'text-red-700' : 'text-green-700'
                          }`}>
                            {remark.action === 'APPROVE' ? '✓ Approved' : '✗ Rejected'}
                          </span>
                          <p className="text-xs text-gray-500 mt-0.5">
                            by {remark.by_user_name}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400">
                          {new Date(remark.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{remark.remark_text}</p>
                    </div>
                  );
                })
              ) : status !== 'REJECTED' && (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No remarks yet</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemarksModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
